import { Trash2, X } from 'lucide-react'
import type { ReactNode } from 'react'
import type { Overlay } from '@shared/types'
import { useEditorStore } from '../../../../store/editorStore'
import { SliderField } from '../../../common/Field'

/**
 * task spec (UI_MK2_PHASE2.md §3-3) 共通ヘッダー/フッター: 種類名・×・削除ボタン
 * (ヘッダー) と 開始秒／尺の数値入力 (フッター)。LogoOverlay は startSec/
 * durationSec を持たない (shared/src/types.ts) ので `startSec`/`durationSec`
 * を渡さなければフッターごと省略される。
 */
export function InspectorFrame({
  title,
  overlayId,
  overlayKind,
  startSec,
  durationSec,
  minDurationSec = 0.2,
  maxDurationSec = 15,
  children,
}: {
  title: string
  overlayId: string
  overlayKind: Overlay['kind']
  startSec?: number
  durationSec?: number
  minDurationSec?: number
  maxDurationSec?: number
  children: ReactNode
}) {
  const select = useEditorStore((s) => s.select)
  const updateOverlay = useEditorStore((s) => s.updateOverlay)
  const removeOverlay = useEditorStore((s) => s.removeOverlay)
  const hasTiming = startSec != null && durationSec != null

  return (
    <div className="flex flex-col h-full" data-testid="overlay-inspector" data-overlay-kind={overlayKind}>
      <div className="flex items-center justify-between p-3 pb-2 shrink-0">
        <div className="text-xs font-semibold">{title}</div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => removeOverlay(overlayId)}
            className="text-text-faint hover:text-accent-red p-1"
            title="削除"
            data-testid="overlay-inspector-delete"
          >
            <Trash2 size={13} />
          </button>
          <button
            onClick={() => select(null)}
            className="text-text-faint hover:text-text p-1"
            title="選択解除"
            data-testid="overlay-inspector-close"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      <div className="px-3 space-y-4">{children}</div>
      {hasTiming && (
        // reviewer minor 1 (UI_MK2_PHASE2.md review): grid-cols-2 で SliderField
        // (レンジ+数値入力の2要素) を横並びにすると、右ペイン最小幅 280px で
        // 「表示長」の数値入力がパネル右端で欠けていた
        // (trial/ui_mk2/review_p2_11_label_inspector.png)。縦積みにして
        // 280〜520px の全パネル幅で欠けないようにする。
        <div className="p-3 mt-3 border-t border-ink-border space-y-3 shrink-0">
          <SliderField
            label="開始"
            value={startSec!}
            min={0}
            max={90}
            step={0.1}
            unit="s"
            onChange={(v) => updateOverlay(overlayId, { startSec: v })}
          />
          <SliderField
            label="表示長"
            value={durationSec!}
            min={minDurationSec}
            max={maxDurationSec}
            step={0.1}
            unit="s"
            onChange={(v) => updateOverlay(overlayId, { durationSec: v })}
          />
        </div>
      )}
    </div>
  )
}
